import { Component, Input, OnInit, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  DataFormActionsComponent,
  DataFormCardComponent,
  DataFormGridComponent,
  ErrorBlockComponent,
} from '@clinic-platform/ui-components';
import { TranslatePipe } from '../../../core/i18n/translate.pipe';
import { CompanyRatingSettingsDto } from '../data-access/settings.models';
import { SettingsService } from '../data-access/settings.service';
import { SETTINGS_TAB_FORM_STYLES } from './settings-tab-form.styles';
import { CompanyScopedSettingsTabBase } from './settings-tab-state.base';

@Component({
  selector: 'app-rating-settings-tab',
  standalone: true,
  imports: [
    DataFormActionsComponent,
    DataFormCardComponent,
    DataFormGridComponent,
    ErrorBlockComponent,
    ReactiveFormsModule,
    TranslatePipe,
  ],
  template: `
    <lib-data-form-card>
      <h3>{{ 'settings.rating.title' | t }}</h3>
      <p class="settings-tab__hint">
        {{ contextText | t }}
      </p>
      <p class="settings-tab__hint">
        {{ 'settings.rating.description' | t }}
      </p>

      <form [formGroup]="ratingForm" (ngSubmit)="saveRating()">
        <lib-data-form-grid>
          <label>
            {{ 'settings.rating.lateCancelPenalty' | t }}
            <input type="number" formControlName="lateCancelPenaltyPercent" min="0" max="100" />
          </label>

          <label>
            {{ 'settings.rating.lateCancelWindowHours' | t }}
            <input type="number" formControlName="lateCancelWindowHours" min="0" max="168" />
          </label>

          <label>
            {{ 'settings.rating.noShowPenalty' | t }}
            <input type="number" formControlName="noShowPenaltyPercent" min="0" max="100" />
          </label>

          <label>
            {{ 'settings.rating.lateArrivalPenalty' | t }}
            <input type="number" formControlName="lateArrivalPenaltyPercent" min="0" max="100" />
          </label>

          <label>
            {{ 'settings.rating.firstClientReschedulePenalty' | t }}
            <input type="number" formControlName="firstClientReschedulePenaltyPercent" min="0" max="100" />
          </label>

          <label>
            {{ 'settings.rating.secondClientReschedulePenalty' | t }}
            <input type="number" formControlName="secondClientReschedulePenaltyPercent" min="0" max="100" />
          </label>

          <label>
            {{ 'settings.rating.thirdPlusClientReschedulePenalty' | t }}
            <input type="number" formControlName="thirdPlusClientReschedulePenaltyPercent" min="0" max="100" />
          </label>

          <label>
            {{ 'settings.rating.attendedOnTimeBonus' | t }}
            <input type="number" formControlName="attendedOnTimeBonusPercent" min="0" max="100" />
          </label>

          <label>
            {{ 'settings.rating.watchThreshold' | t }}
            <input type="number" formControlName="watchThresholdPercent" min="0" max="100" />
          </label>

          <label>
            {{ 'settings.rating.riskThreshold' | t }}
            <input type="number" formControlName="riskThresholdPercent" min="0" max="100" />
          </label>

          <label class="settings-tab__checkbox">
            <input type="checkbox" formControlName="manualAdjustmentAllowed" />
            {{ 'settings.rating.manualAdjustmentAllowed' | t }}
          </label>

          <label class="settings-tab__checkbox">
            <input
              type="checkbox"
              formControlName="manualAdjustmentReasonRequired"
              [disabled]="!ratingForm.controls.manualAdjustmentAllowed.value"
            />
            {{ 'settings.rating.manualAdjustmentReasonRequired' | t }}
          </label>
        </lib-data-form-grid>

        <lib-data-form-actions>
          <button type="submit" [disabled]="ratingForm.invalid || !canSaveSettings">
            {{ isSaving ? ('Saving...' | t) : ('settings.rating.save' | t) }}
          </button>
        </lib-data-form-actions>
      </form>

      @if (errorText) {
        <lib-error-block variant="error" [message]="(errorText | t) ?? ''" />
      }

      @if (successText) {
        <lib-error-block variant="success" [message]="(successText | t) ?? ''" />
      }
    </lib-data-form-card>
  `,
  styles: [SETTINGS_TAB_FORM_STYLES],
})
export class RatingSettingsTabComponent extends CompanyScopedSettingsTabBase implements OnInit
{
  @Input() public contextText = '';

  private readonly settingsService = inject(SettingsService);
  private readonly formBuilder = inject(FormBuilder);

  protected readonly ratingForm = this.formBuilder.group({
    lateCancelPenaltyPercent: this.formBuilder.nonNullable.control(10, [
      Validators.required,
      Validators.min(0),
      Validators.max(100),
    ]),
    lateCancelWindowHours: this.formBuilder.nonNullable.control(24, [
      Validators.required,
      Validators.min(0),
      Validators.max(168),
    ]),
    noShowPenaltyPercent: this.formBuilder.nonNullable.control(25, [
      Validators.required,
      Validators.min(0),
      Validators.max(100),
    ]),
    lateArrivalPenaltyPercent: this.formBuilder.nonNullable.control(5, [
      Validators.required,
      Validators.min(0),
      Validators.max(100),
    ]),
    firstClientReschedulePenaltyPercent: this.formBuilder.nonNullable.control(5, [
      Validators.required,
      Validators.min(0),
      Validators.max(100),
    ]),
    secondClientReschedulePenaltyPercent: this.formBuilder.nonNullable.control(10, [
      Validators.required,
      Validators.min(0),
      Validators.max(100),
    ]),
    thirdPlusClientReschedulePenaltyPercent: this.formBuilder.nonNullable.control(15, [
      Validators.required,
      Validators.min(0),
      Validators.max(100),
    ]),
    attendedOnTimeBonusPercent: this.formBuilder.nonNullable.control(2, [
      Validators.required,
      Validators.min(0),
      Validators.max(100),
    ]),
    manualAdjustmentAllowed: this.formBuilder.nonNullable.control(true),
    manualAdjustmentReasonRequired: this.formBuilder.nonNullable.control(true),
    watchThresholdPercent: this.formBuilder.nonNullable.control(85, [
      Validators.required,
      Validators.min(0),
      Validators.max(100),
    ]),
    riskThresholdPercent: this.formBuilder.nonNullable.control(60, [
      Validators.required,
      Validators.min(0),
      Validators.max(100),
    ]),
  });

  public ngOnInit(): void
  {
    this.reloadSettings();
  }

  protected saveRating(): void
  {
    if (this.ratingForm.invalid || !this.canSaveSettings) {
      return;
    }

    const value = this.ratingForm.getRawValue();
    const manualAdjustmentReasonRequired = value.manualAdjustmentAllowed
      ? value.manualAdjustmentReasonRequired
      : false;

    if (value.riskThresholdPercent > value.watchThresholdPercent) {
      this.setError('settings.rating.error.thresholdOrder');
      return;
    }

    this.beginSaving();

    this.currentSettingsRequest(this.settingsService.updateRatingSettings({
      lateCancelPenaltyPercent: value.lateCancelPenaltyPercent,
      lateCancelWindowHours: value.lateCancelWindowHours,
      noShowPenaltyPercent: value.noShowPenaltyPercent,
      lateArrivalPenaltyPercent: value.lateArrivalPenaltyPercent,
      firstClientReschedulePenaltyPercent: value.firstClientReschedulePenaltyPercent,
      secondClientReschedulePenaltyPercent: value.secondClientReschedulePenaltyPercent,
      thirdPlusClientReschedulePenaltyPercent: value.thirdPlusClientReschedulePenaltyPercent,
      attendedOnTimeBonusPercent: value.attendedOnTimeBonusPercent,
      manualAdjustmentAllowed: value.manualAdjustmentAllowed,
      manualAdjustmentReasonRequired,
      watchThresholdPercent: value.watchThresholdPercent,
      riskThresholdPercent: value.riskThresholdPercent,
    }, this.companyId)).subscribe({
      next: (settings: CompanyRatingSettingsDto) => {
        this.ratingForm.patchValue(settings);
        this.setSuccess('settings.rating.success.saved');
        this.finishSaving();
      },
      error: (error: unknown) => {
        this.setHttpError(error);
        this.finishSaving();
      },
    });
  }

  protected reloadSettings(): void
  {
    this.loadSettings(this.settingsService.getRatingSettings(this.companyId), this.ratingForm).subscribe({
      next: (settings: CompanyRatingSettingsDto) => {
        this.ratingForm.patchValue(settings);
      },
      error: (error: unknown) => {
        this.setHttpError(error);
      },
    });
  }
}
